import { useCallback, useMemo, useState } from 'react';
import { BomVersionMeta } from '../../../types/bomVersions';

/**
 * Decides whether the Smart BOM reveal moment should show on the discovery
 * sub-step, from the REAL version list — never invented numbers.
 *
 * The reveal is earned: it appears only when the BOM has genuinely improved
 * since upload (≥2 versions AND at least one recorded change beyond the v1
 * "Extracted" baseline), and only ONCE per version state — the latest version
 * the buyer saw (or dismissed) is remembered per BOM in localStorage, so a
 * NEW version since then may bring the card back. Storage reads/writes are
 * best-effort: a cleared store just shows the moment again, which is fine.
 */

const storageKey = (bomId: string) => `smartBomRevealSeen:${bomId}`;

function readSeenVersion(bomId: string): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey(bomId));
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeSeenVersion(bomId: string, version: number): void {
  try {
    window.localStorage.setItem(storageKey(bomId), String(version));
  } catch {
    // Best-effort memory only — losing it re-shows the card, never breaks it.
  }
}

export interface SmartBomRevealState {
  /** True when the reveal card should render right now. */
  show: boolean;
  /** Latest version number (0 until the list has loaded — only read when `show`). */
  latestVersion: number;
  /** Sum of recorded changes across every version after the v1 baseline. */
  changeCount: number;
  /** Marks the current version state as seen (both "see it" and "not now"). */
  acknowledge: () => void;
}

export function useSmartBomReveal(
  bomId: string | null,
  versions: BomVersionMeta[] | null,
): SmartBomRevealState {
  // Keyed by bomId + version so a later version re-arms the card even within
  // one mounted session, mirroring what localStorage does across sessions.
  const [ackedKey, setAckedKey] = useState<string | null>(null);

  const latestVersion =
    versions && versions.length > 0 ? versions[versions.length - 1].version : null;

  const changeCount = useMemo(
    () =>
      (versions || [])
        .filter((v) => v.version > 1)
        .reduce((sum, v) => sum + (v.changeCount || 0), 0),
    [versions],
  );

  const seenVersion = useMemo(
    () => (bomId ? readSeenVersion(bomId) : null),
    // Re-read when a fresh version lands, so `show` compares against storage.
    [bomId, latestVersion],
  );

  const acknowledge = useCallback(() => {
    if (!bomId || latestVersion === null) return;
    writeSeenVersion(bomId, latestVersion);
    setAckedKey(`${bomId}:${latestVersion}`);
  }, [bomId, latestVersion]);

  const show =
    !!bomId &&
    !!versions &&
    versions.length >= 2 &&
    latestVersion !== null &&
    changeCount > 0 &&
    (seenVersion === null || seenVersion < latestVersion) &&
    ackedKey !== `${bomId}:${latestVersion}`;

  return { show, latestVersion: latestVersion ?? 0, changeCount, acknowledge };
}

export default useSmartBomReveal;
